import { useEffect, useMemo, useState } from 'react';
import { deleteFromOutbox, type OutboxPost } from './outbox';
import type { RetryOneResult } from './useOutbox';

/**
 * outbox 一覧の楽観 UI (REQ-4.1.15、Phase 5 完成版)。
 *
 * AppHome の投稿一覧の上に「未送信」section として表示:
 *   - title / comment / photo preview (Blob から ObjectURL 生成) / lastError 表示
 *   - 取消 button (個別 deleteFromOutbox、useOutbox の BroadcastChannel 経由で count 更新)
 *
 * photo Blob は ObjectURL に変換して preview。useEffect で生成 + cleanup (revoke)。
 *
 * R-28 第 1 弾 (c088aa05) で実装済:
 *   - pending / failed badge 分離 (MAX_ATTEMPTS 超過で 'failed' マーク、danger 系背景)
 *   - header「(保留 N / 失敗 M)」サブカウント表示
 *
 * R-28 後続 (Phase 5):
 *   - retry 単発 button (個別 sendOutboxItem)
 *   - 指数 back-off / expiration / auto retry on online で failed skip
 */

export interface OutboxListProps {
  items: OutboxPost[];
  /** 削除 (取消) 成功時のコールバック (UI 通知用)。 */
  onCancelled?: (id: number) => void;
  /**
   * 個別 retry コールバック (R-28 第 3 弾)。useOutbox.retryOne を直接渡す想定。
   * 戻り値の ok で成功/失敗を判定して toast 表示等を行う (本コンポーネントは call のみ)。
   * 省略時は failed item の「再試行」button を非表示。
   */
  onRetry?: (id: number) => Promise<RetryOneResult>;
}

interface ItemPreviewProps {
  item: OutboxPost;
  onCancel: (id: number) => void;
  onRetry?: (id: number) => Promise<RetryOneResult>;
}

function OutboxItemRow({ item, onCancel, onRetry }: ItemPreviewProps) {
  const [retrying, setRetrying] = useState(false);
  // photoThumbBlob を ObjectURL に変換して preview 表示。unmount で revoke。
  const url = useMemo(() => {
    if (!item.photoThumbBlob) return null;
    return URL.createObjectURL(item.photoThumbBlob);
  }, [item.photoThumbBlob]);

  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [url]);

  const createdLocal = new Date(item.createdLocalAt).toLocaleString('ja-JP');
  // R-28 第 1 弾: status='failed' (MAX_ATTEMPTS 超過) は背景 + badge を danger 系に分離。
  const isFailed = item.status === 'failed';

  return (
    <li
      className={`mb-2 p-2 rounded-md ${
        isFailed ? 'bg-danger-50 border border-error-300' : 'bg-notice-50 border border-warning-300'
      }`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
            isFailed ? 'bg-danger-600 text-white' : 'bg-warning-300 text-warning-800'
          }`}
        >
          {isFailed ? '🔴 送信失敗' : '📤 未送信'}
        </span>
        <strong className="text-slate-900">{item.title}</strong>
        {url && (
          <img
            src={url}
            alt={`${item.title} のプレビュー`}
            className="w-[40px] h-[40px] object-cover rounded border border-slate-300"
          />
        )}
        {item.attempts > 0 && (
          <span className="text-[10px] text-danger-600">⚠ {item.attempts} 回失敗</span>
        )}
        <span className="text-slate-400 text-xs">by {item.authorNickname}</span>
        <span className="text-slate-400 text-xs">作成: {createdLocal}</span>
        {/* R-28 第 3 弾: failed item に「再試行」button (onRetry 渡された場合のみ) */}
        {isFailed && onRetry && item.id !== undefined && (
          <button
            type="button"
            disabled={retrying}
            onClick={async () => {
              if (item.id === undefined) return;
              setRetrying(true);
              try {
                await onRetry(item.id);
              } finally {
                setRetrying(false);
              }
            }}
            aria-label={`${item.title} の未送信投稿を再送信`}
            className="text-xs text-primary-700 hover:text-primary-800 underline bg-transparent border-none p-0 cursor-pointer ml-auto disabled:opacity-50 disabled:cursor-wait"
          >
            {retrying ? '送信中...' : '再試行'}
          </button>
        )}
        <button
          type="button"
          onClick={async () => {
            if (item.id === undefined) return;
            if (!confirm(`「${item.title}」の送信を取消しますか?`)) return;
            await deleteFromOutbox(item.id);
            onCancel(item.id);
          }}
          aria-label={`${item.title} の未送信投稿を取消`}
          className={`text-xs text-danger-600 hover:text-danger-700 underline bg-transparent border-none p-0 cursor-pointer ${
            isFailed && onRetry ? 'ml-2' : 'ml-auto'
          }`}
        >
          取消
        </button>
      </div>
      {item.comment && <div className="text-slate-600 text-[13px] mt-1 ml-1">{item.comment}</div>}
      {item.lastError && (
        <div className="text-danger-600 text-[11px] mt-1 ml-1">最後のエラー: {item.lastError}</div>
      )}
    </li>
  );
}

export function OutboxList({ items, onCancelled, onRetry }: OutboxListProps) {
  const [, forceRerender] = useState(0);

  if (items.length === 0) return null;

  // R-28 第 1 弾: pending / failed 件数を header で分離表示
  const failedCount = items.filter((i) => i.status === 'failed').length;
  const pendingCount = items.length - failedCount;

  return (
    <div className="mt-3">
      <h3 className="text-xs font-bold text-notice-700 mb-1">
        📤 未送信の投稿 ({items.length})
        {failedCount > 0 && (
          <span className="ml-2 text-danger-700">
            (保留 {pendingCount} / <strong>失敗 {failedCount}</strong>)
          </span>
        )}
      </h3>
      <ul className="pl-0 m-0 list-none">
        {items.map((item) => (
          <OutboxItemRow
            key={item.id}
            item={item}
            onCancel={(id) => {
              onCancelled?.(id);
              // BroadcastChannel 経由で useOutbox state が refresh されるが、明示的に再描画
              forceRerender((n) => n + 1);
            }}
            onRetry={onRetry}
          />
        ))}
      </ul>
    </div>
  );
}
